import { useState } from "react";
import { Link } from "react-router-dom";
import TaskCard from "@/components/TaskCard";
import { ArrowRight } from "lucide-react";

const AVAILABLE_COL = {
  key: "available",
  title: "Available",
  subtitle: "Open challenge rounds",
};

const ENTRY_COLS = [
  {
    key: "in_progress",
    title: "In Progress",
    subtitle: "Entered — do your work",
  },
  {
    key: "submitted",
    title: "Submitted",
    subtitle: "Waiting for AI ranking",
  },
  {
    key: "ranked",
    title: "Ranked",
    subtitle: "See your score & rank",
  },
  { key: "won", title: "Completed", subtitle: "Payout received" },
];

/**
 * Horizontally-scrollable 5-column pipeline board.
 *
 * Props:
 *   available:  array of task objects (Available column). If null → column hidden (for /app/applications).
 *   pipeline:   { entered:[], selected:[], completed:[], paid:[] } — entries grouped by status
 *   onEnter:    handler(task) fired when user clicks Enter round
 *   completedCount: number displayed in header caption
 *   emptyAvailableCta: React node shown when Available column is empty (only if available prop is not null)
 *   hideEmptyMiddleCols: when true, the three middle columns (entered/selected/completed) are
 *     hidden entirely if they have 0 items.
 *   hideEmptyPaidCol: when true, the `paid` column is also hidden when empty. Combine with
 *     hideEmptyMiddleCols on views (like /app/applications) where NO column should ever
 *     appear as an empty skeleton.
 */
export default function PipelineBoard({
  available,
  pipeline,
  onEnter,
  completedCount = 0,
  emptyAvailableCta,
  hideEmptyMiddleCols = false,
  hideEmptyPaidCol = false,
  hideHeader = false,
}) {
  const showAvailable = available !== null && available !== undefined;

  // Filter entry columns based on the two hide-when-empty flags.
  const visibleEntryCols = ENTRY_COLS.filter((c) => {
    const list = (pipeline && pipeline[c.key]) || [];
    if (c.key === "won") {
      return hideEmptyPaidCol ? list.length > 0 : true;
    }
    return hideEmptyMiddleCols ? list.length > 0 : true;
  });

  return (
    <section data-testid="pipeline-board">
      {!hideHeader && (
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Your challenge pipeline
            </h2>
          </div>
        </div>
      )}

      {/* Assemble the full list of visible columns for the mobile tab bar */}
      {(() => {
        const allCols = [
          ...(showAvailable ? [{ ...AVAILABLE_COL, list: available }] : []),
          ...visibleEntryCols.map((c) => ({ ...c, list: (pipeline && pipeline[c.key]) || [] })),
        ];
        return (
          <>
            {/* MOBILE — tab bar + single column */}
            <MobilePipeline
              columns={allCols}
              onEnter={onEnter}
              emptyAvailableCta={emptyAvailableCta}
            />

            {/* DESKTOP — original horizontally-scroll multi-column layout */}
            <div
              className="hidden overflow-x-auto pb-2 md:block"
              style={{ scrollbarWidth: "thin" }}
              data-testid="pipeline-scroll"
            >
              <div className="flex gap-4" style={{ minWidth: "min-content" }}>
                {allCols.map((c) => (
                  <Column key={c.key} title={c.title} subtitle={c.subtitle} count={c.list.length} testKey={c.key}>
                    {c.list.length === 0 ? (
                      c.key === "available"
                        ? (emptyAvailableCta || <EmptyBox label="No open rounds right now" />)
                        : <EmptyBox label={_emptyLabel(c.key)} />
                    ) : c.list.map((it) => (
                      c.key === "available"
                        ? <TaskCard key={it.task_id} variant="available" task={it} onEnter={onEnter} />
                        : <TaskCard key={it.entry_id} variant="entry" entry={it} />
                    ))}
                  </Column>
                ))}
              </div>
            </div>
          </>
        );
      })()}
    </section>
  );
}

function _emptyLabel(key) {
  return key === "in_progress" ? "Nothing in progress"
    : key === "submitted" ? "No submissions pending"
    : key === "ranked" ? "No results yet"
    : key === "won" ? "No completed tasks yet"
    : "Empty";
}

function MobilePipeline({ columns, onEnter, emptyAvailableCta }) {
  const [activeIdx, setActiveIdx] = useState(() => {
    // Default to the first non-empty column, or 0
    const firstNonEmpty = columns.findIndex((c) => (c.list || []).length > 0);
    return firstNonEmpty >= 0 ? firstNonEmpty : 0;
  });
  const active = columns[activeIdx];
  if (!active) return null;
  return (
    <div className="md:hidden" data-testid="pipeline-mobile">
      {/* Tab bar */}
      <div className="mb-3 flex gap-1 overflow-x-auto rounded-lg border border-neutral-200 bg-neutral-50 p-1" style={{ scrollbarWidth: "none" }}>
        {columns.map((c, i) => (
          <button
            key={c.key}
            onClick={() => setActiveIdx(i)}
            className={`shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              i === activeIdx ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500"
            }`}
            data-testid={`pipeline-mobile-tab-${c.key}`}
          >
            {c.title}
            <span className="ml-1.5 rounded-full bg-neutral-200 px-1.5 py-0.5 text-[10px] tabular-nums text-neutral-700">
              {(c.list || []).length}
            </span>
          </button>
        ))}
      </div>
      {/* Single-column card list */}
      <div className="space-y-3">
        {(active.list || []).length === 0 ? (
          active.key === "available"
            ? (emptyAvailableCta || <EmptyBox label="No open rounds right now" />)
            : <EmptyBox label={_emptyLabel(active.key)} />
        ) : active.list.map((it) => (
          active.key === "available"
            ? <TaskCard key={it.task_id} variant="available" task={it} onEnter={onEnter} />
            : <TaskCard key={it.entry_id} variant="entry" entry={it} />
        ))}
      </div>
    </div>
  );
}

function Column({ title, subtitle, count, children, testKey }) {
  return (
    <div
      className="shrink-0 rounded-2xl bg-neutral-100/60 p-3"
      style={{ width: "320px" }}
      data-testid={`pipeline-col-${testKey}`}
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
        <span
          className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-white px-2 text-xs font-semibold text-neutral-600 shadow-sm"
          data-testid={`col-count-${testKey}`}
        >
          {count}
        </span>
      </div>
      <p className="mb-3 px-1 text-xs text-neutral-500">{subtitle}</p>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function EmptyBox({ label }) {
  return (
    <div className="flex items-center justify-center rounded-lg border border-dashed border-neutral-300 bg-white/60 py-8 text-xs text-neutral-400">
      {label}
    </div>
  );
}

/** Shared "Browse tasks" CTA used in the Applications page when there are no
 *  available tasks column. */
export function BrowseTasksCta() {
  return (
    <Link
      to="/app/tasks"
      data-testid="browse-tasks-cta"
      className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-amber-300 bg-white p-6 text-center transition hover:bg-amber-50"
    >
      <p className="text-sm font-semibold text-amber-800">Browse tasks</p>
      <p className="text-xs text-neutral-500">
        Enter a challenge round to fill your pipeline
      </p>
      <ArrowRight className="h-4 w-4 text-amber-700" />
    </Link>
  );
}
